"use client";

import { cn, variantBackgroundColors } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";

interface ContentBlockProps {
  badge?: string;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  className?: string;
}

interface Feature9Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;  
  themeChangeOnScroll?: boolean;
  badge?: string;
  title?: string;
  content?: string;
  leftContent?: ContentBlockProps;
  rightContent?: ContentBlockProps;
  attributes?: React.HTMLProps<HTMLElement>;
}

const ContentBlock = ({
  badge,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  className
}: ContentBlockProps) => (
  <div className={cn(className, "flex flex-col justify-between gap-10 lg:gap-20 text-center p-6 rounded-lg")}>
    {badge && (
      <Badge variant="outline" className="m-auto">
        {badge}
      </Badge>
    )}
    <div className="flex flex-col gap-4">
    {title && (
      <h4>
        {parse(title)}
      </h4>
    )}
    {content && (
      <div className="text-muted-foreground paragraph-lg [&_strong]:text-primary">
        {parse(content)}  
      </div>
    )}
    </div>
    {hasButton && buttonText && (
      <div className="flex flex-col gap-4 m-auto items-center">
        <Button 
          variant={buttonStyle}
          size="lg"
          asChild
        >
          <Link href={buttonLink ?? ""}>
            {buttonText}
          </Link>
        </Button>
        {microCopy && (
          <p className="text-muted-foreground paragraph-sm text-center">
            {microCopy}
          </p>
        )}
      </div>
    )}
  </div>
);

const Feature9 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 9",
  themeChangeOnScroll = false,
  badge,
  title,
  content,
  leftContent,
  rightContent,
  attributes,
}: Feature9Props) => {

  const contentBlockBackground = variantBackgroundColors(darkMode, variant);

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature9"
      attributes={attributes}
    >
      <div className="container">
        <div className="flex flex-col gap-6 text-center justify-center" data-aos="fade-up">
          {badge && (
            <Badge variant="outline" bold className="m-auto">
              {badge}
            </Badge>
          )}
          {title && (
            <h2 className="max-w-[500px] lg:max-w-[800px] mx-auto">
              {parse(title)}
            </h2>
          )}
          {content && (
            <div className="paragraph-lg text-muted-foreground max-w-[58ch] mx-auto">
              {parse(content)}
            </div>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-2 mt-10 lg:mt-24" data-aos="fade-up" data-aos-delay="100">
          <ContentBlock {...leftContent} className={contentBlockBackground} />
          <ContentBlock {...rightContent} className={contentBlockBackground} />
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Feature9 };



